
import React, { useState, useEffect, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useStateContext } from '../../../contexts/ContextProvider';
import { useWebSocket } from '../../../hooks/useWebSocket';
import { useAuth } from '../../../contexts/AuthContext';
import { services } from '../../../services/api';

import SmartChart from '../../../components/SmartChart/SmartChart';
import SmartOrderForm from '../../../components/SmartTrade/SmartOrderForm';
import SmartTradeErrorBoundary from '../../../components/SmartTrade/SmartTradeErrorBoundary';
import SmartTradeSettings from '../../../components/SmartTrade/SmartTradeSettings';
import MarketDepth from '../../../components/SmartTrade/MarketDepth';
import { Segmented, Breadcrumb } from '../../../components/ds';

//* 50-19 — per-user chart-type preference for the graph-less watchlist chart (ids = SmartChart's own)
const CHART_TYPE_KEY = 'smarttrade.chartType';
const CHART_TYPES = ['candle', 'line', 'heikin', 'area'];
const readChartType = () => {
  try {
    const v = localStorage.getItem(CHART_TYPE_KEY);
    return CHART_TYPES.includes(v) ? v : 'candle';
  } catch (e) { return 'candle'; }
};

const SmartTrade = () => {
  const { state } = useLocation();
  const navigate = useNavigate();
  const { setActiveMenu, currentColor } = useStateContext();
  
  // Instrument State
  const [instrument, setInstrument] = useState(state?.instrument || null);
  const [loading, setLoading] = useState(true);
  //--> Right-column panel tabs (Depth / Order / Algo) — each gets full height.
  const [rightTab, setRightTab] = useState('depth');
  //--> Chart type (no graph here, so SmartChart's default-series fallback renders it)
  const [chartType, setChartTypeState] = useState(readChartType);
  const setChartType = (t) => {
    setChartTypeState(t);
    try { localStorage.setItem(CHART_TYPE_KEY, t); } catch (e) { /* storage unavailable */ }
  };

  // WebSocket for Real-time Ticks
  const { subscribeTopic, isConnected } = useWebSocket({
    page_id: 'smart_trade',
    autoConnect: true
  });

  // Redirect if no instrument provided
  useEffect(() => {
    if (!instrument) {
        // Fallback or redirect
        console.warn("No instrument provided to SmartTrade, redirecting...");
        navigate('/WatchList');
    } else {
        setLoading(false);
    }
  }, [instrument, navigate]);

  // Hide Sidebar on Mount
  useEffect(() => {
    setActiveMenu(false);
  }, []);

  // Auth Context for Tier Gating
  const { user } = useAuth();

  // Tick Subscription
  useEffect(() => {
    if (!instrument?.instrument_id || !isConnected) return;

    // Check user tier for HQ data access (e.g., 'ultra', 'pro', or any paid tier)
    // Assuming 'free' users get standard quality and others get HQ
    const isPaidTier = user?.subscription_plan && user.subscription_plan.toLowerCase() !== 'free';
    //! V12B-11: `exchange` is a CLIENT-SIDE hint — the manager strips it before the wire,
    //! so it never reaches the backend's subscribe contract alongside HQ. It tells the
    //! delivery-stall watchdog which venue's hours govern this instrument's silence.
    const subOptions = {
      ...(isPaidTier ? { HQ: true } : {}),
      exchange: instrument.exchange,
    };

    if (isPaidTier) {
        console.log(`[SmartTrade] Subscribing to HQ ticks for ${instrument.symbol} (Tier: ${user.subscription_plan})`);
    }

    const topic = `market.${instrument.instrument_id}.tick`;
    const unsub = subscribeTopic(topic, (msg) => {
        const tick = msg.data || msg;
        // Debug Log
        if (tick.bid_prices) {
             console.log("[SmartTrade] Tick with Depth:", {
                 bid: tick.bid_prices[0],
                 ask: tick.ask_prices[0]
             });
        }
        
        // Update local instrument state with latest price and depth
        setInstrument(prev => ({
            ...prev,
            ...tick, // Merge full tick data (includes depth arrays)
            ltp: tick.ltp || tick.last_price || prev.ltp, // Ensure LTP preference
            change_percent: tick.change_percent || prev.change_percent
        }));
    }, subOptions); // <--- Pass subscription options here

    return () => {
        if (unsub) unsub();
    };
  }, [instrument?.instrument_id, isConnected, subscribeTopic, user?.subscription_plan]);


  if (loading || !instrument) return <div className="p-10 text-center">Loading Trade Interface...</div>;

  return (
    <div className="flex flex-col bg-main-bg dark:bg-main-dark-bg lg:h-screen lg:overflow-hidden animate-in fade-in duration-500">
        {/* Header — slim instrument bar. Breadcrumb (its "Watchlist" crumb is the
            back nav, so no separate back button) + exchange badge on the left, live
            price on the right. The chart keeps its own symbol/exchange pill for
            embedded (strategy / backtest) contexts that have no title bar. */}
        <div className="h-10 shrink-0 border-b border-[var(--divider)] bg-white dark:bg-bg-2 flex items-center justify-between gap-4 px-4">
            <div className="flex items-center gap-2 min-w-0">
                <Breadcrumb items={[
                    { label: "Watchlist", to: "/WatchList" },
                    { label: instrument.symbol },
                ]} />
                <span className="text-[10px] px-1.5 py-0.5 rounded bg-bg-3 text-fg-3 font-bold shrink-0">
                    {instrument.exchange}
                </span>
            </div>

            {/* Live price — kept in persistent chrome (always glanceable) */}
            <div className="flex items-baseline gap-2 shrink-0 font-mono tabular-nums">
                <span className={`text-base font-black ${
                    (instrument.change_percent || 0) >= 0 ? 'text-profit' : 'text-loss'
                }`}>
                    {instrument.ltp?.toFixed(2) || '---'}
                </span>
                <span className={`text-xs font-bold ${
                    (instrument.change_percent || 0) >= 0 ? 'text-profit' : 'text-loss'
                }`}>
                    {(instrument.change_percent || 0) >= 0 ? '+' : ''}{(instrument.change_percent || 0).toFixed(2)}%
                </span>
            </div>
        </div>

        {/* Main Grid Content */}
        <div className="flex-1 lg:overflow-hidden p-2 grid grid-cols-12 gap-2">
            
            {/* LEFT: Chart Area (9 cols - Wider) */}
            <div className="col-span-12 lg:col-span-9 h-[46vh] lg:h-auto bg-white dark:bg-bg-2 rounded-xl shadow-sm border border-[var(--divider)] overflow-hidden relative">
                {/* SmartChart sizes ITSELF via heightClass (default h-[50vh]) — it does
                    not fill its parent. Inside the phone wrapper's h-[46vh] +
                    overflow-hidden that 50vh overhang clipped the bottom strip (time
                    axis + volume). Fill the container on phone; keep the desktop 50vh. */}
                {/* F-NEW-1/A2 (QA iter 3) -- manual single-instrument trade view has no
                    strategy/isContinuous concept in scope; pass the hard-gated `false`
                    explicitly so resolveRolloverDays() never falls through to a stray
                    instrument.rollover_days here either (LB-8's structural gate). */}
                <SmartChart instrument={instrument} liveTick={instrument} heightClass="h-full lg:h-[50vh]" isContinuous={false} chartType={chartType} onChartTypeChange={setChartType} />
            </div>

            {/* RIGHT: Trading Controls (3 cols) — TABBED so each section gets the
                full column height (Order / Depth / Algo). All three stay mounted
                (hidden when inactive) to preserve in-progress order state. */}
            <div className="col-span-12 lg:col-span-3 flex flex-col gap-2 lg:h-full lg:overflow-hidden pb-1">

                <Segmented
                    aria-label="Trading panel"
                    value={rightTab}
                    onChange={setRightTab}
                    options={[
                        { value: 'depth', label: 'Depth' },
                        { value: 'order', label: 'Order' },
                        { value: 'algo',  label: 'Algo' },
                    ]}
                />

                <div className="flex-1 lg:h-0 bg-white dark:bg-bg-2 rounded-xl shadow-sm border border-[var(--divider)] p-2 lg:overflow-y-auto custom-scrollbar">
                    {/* Order entry */}
                    <div className={rightTab === 'order' ? '' : 'hidden'}>
                        <SmartTradeErrorBoundary>
                            <SmartOrderForm instrument={instrument} />
                        </SmartTradeErrorBoundary>
                    </div>

                    {/* Market depth */}
                    <div className={rightTab === 'depth' ? '' : 'hidden'}>
                        <MarketDepth
                            depth={{
                                bid_prices: instrument.bid_prices,
                                bid_qtys: instrument.bid_qtys,
                                bid_orders: instrument.bid_orders,
                                ask_prices: instrument.ask_prices,
                                ask_qtys: instrument.ask_qtys,
                                ask_orders: instrument.ask_orders
                            }}
                            totalBuyQty={instrument.total_buy_qty}
                            totalSellQty={instrument.total_sell_qty}
                        />
                    </div>

                    {/* Algo strategy */}
                    <div className={rightTab === 'algo' ? '' : 'hidden'}>
                        <SmartTradeSettings context={{ kind: 'instrument', instrumentId: instrument.instrument_id }} />
                    </div>
                </div>

            </div>
        </div>
    </div>
  );
};

export default SmartTrade;
